import { z } from 'zod';
import { LEVEL_DESCRIPTION_MAX_LENGTH, LevelSchema } from './level.js';

/**
 * Contrat de l'API REST de contenu (spec §6/§7) — la frontière entre SQLite et
 * le jeu.
 *
 * Le client ne connaît plus les niveaux par des fichiers importés : il lit ce
 * catalogue, puis charge le JSON d'un niveau par son identifiant. Les niveaux
 * `draft` ne sont servis qu'avec le Bearer token (`?drafts=1`).
 */

/** Statut d'un niveau en base : brouillon soumis par le MCP, ou publié. */
export const LevelStatusSchema = z.enum(['draft', 'published']);
export type LevelStatus = z.infer<typeof LevelStatusSchema>;

/** Résumé d'un niveau pour la sélection — sans la carte ASCII. */
export const LevelSummarySchema = z.object({
  id: z.string().min(1),
  game: z.string().min(1),
  world: z.string().min(1),
  chapter: z.number().int().positive(),
  level: z.number().int().positive(),
  name: z.string().min(1),
  /** L'histoire du niveau (`meta.description`), quand il en porte une. */
  description: z.string().min(1).max(LEVEL_DESCRIPTION_MAX_LENGTH).optional(),
  parTimeSeconds: z.number().positive(),
  mechanics: z.array(z.string()),
  author: z.string().min(1),
  status: LevelStatusSchema,
  /** Niveau livré avec le code (« seed ») plutôt que soumis via le MCP. */
  seeded: z.boolean(),
  updatedAt: z.string().min(1),
});

export const ChapterSummarySchema = z.object({
  chapter: z.number().int().positive(),
  name: z.string().min(1),
  difficulty: z.number().int().min(1).max(5),
  mechanics: z.array(z.string()),
  /** Passages coopératifs exigés par le chapitre (spec §8, métrique 5). */
  minCoopPassages: z.number().int().nonnegative(),
  levels: z.array(LevelSummarySchema),
});

export const WorldSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  theme: z.string().min(1),
  description: z.string(),
  chapters: z.array(ChapterSummarySchema),
});

/** Réponse de `GET /api/content`. */
export const ContentCatalogSchema = z.object({
  game: z.string().min(1),
  /** Vrai si la réponse inclut les brouillons (`?drafts=1` + Bearer). */
  includesDrafts: z.boolean(),
  worlds: z.array(WorldSummarySchema),
});

/** Réponse de `GET /api/levels/:id`. */
export const LevelResponseSchema = z.object({
  status: LevelStatusSchema,
  seeded: z.boolean(),
  updatedAt: z.string().min(1),
  level: LevelSchema,
});

/** Une entrée de `GET /api/games`. */
export const GameSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
});

export const GamesResponseSchema = z.object({
  games: z.array(GameSummarySchema),
});

export type LevelSummary = z.infer<typeof LevelSummarySchema>;
export type ChapterSummary = z.infer<typeof ChapterSummarySchema>;
export type WorldSummary = z.infer<typeof WorldSummarySchema>;
export type ContentCatalog = z.infer<typeof ContentCatalogSchema>;
export type LevelResponse = z.infer<typeof LevelResponseSchema>;
export type GameSummary = z.infer<typeof GameSummarySchema>;
export type GamesResponse = z.infer<typeof GamesResponseSchema>;

/** Retrouve un niveau du catalogue par son identifiant. */
export function findCatalogLevel(
  catalog: ContentCatalog,
  levelId: string,
): LevelSummary | undefined {
  for (const world of catalog.worlds) {
    for (const chapter of world.chapters) {
      for (const level of chapter.levels) {
        if (level.id === levelId) {
          return level;
        }
      }
    }
  }
  return undefined;
}

/** Tous les niveaux du catalogue, dans l'ordre world → chapitre → niveau. */
export function catalogLevels(catalog: ContentCatalog): LevelSummary[] {
  return catalog.worlds.flatMap((world) => world.chapters.flatMap((chapter) => chapter.levels));
}
